import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateRangeSelector } from '@components/DateRangeSelector';
import PatientPicker from '@components/Patient/PatientPicker';
import { type Clinician, useClinicians } from '@features/organizations';
import { useSubjectLanguageVariants } from '@features/user';
import HistoryIcon from '@mui/icons-material/History';
import { Autocomplete, Box, Chip, Skeleton, TextField, Tooltip, Typography } from '@mui/material';

import type { Patient } from '../../../../../types/types';
import type { ClaimV2Item } from '../../../store';
import {
  useBatchClaimSubmit,
  useBulkClaimX12Download,
  useClaimsV2Query,
} from '../../../store';
import { formatDateOnly } from '../../../utils';
import { ClaimReviewWorkspace } from '../claims-review';
import {
  AttentionDispositionChip,
  AttentionPulseDot,
  CellContent,
  ClaimBatchProgressTray,
  type ColumnDef,
  DataTable,
  formatCents,
  getDispositionState,
  Pagination,
  PayerFilter,
  SegmentControl,
  type SelectionAction,
  SelectionToolbar,
  TableToolbar,
  useBillingPayerOptionsQuery,
  useColumnPreferences,
  useTabFilters,
} from '../shared';

import { ClaimRowActions } from './ClaimRowActions';

const getDefaultColumns = (singularCap: string): ColumnDef[] => [
  { key: 'service_date', label: 'Date', width: '80px', required: true },
  { key: 'patient_name', label: singularCap, width: '1.2fr', type: 'patient', required: true },
  { key: 'provider_name', label: 'Provider', width: '1fr' },
  { key: 'payer_name', label: 'Payer', width: '1fr' },
  { key: 'what_to_do', label: 'What to do', width: '2fr', type: 'action', required: true },
  { key: 'amount', label: 'Amount', width: '90px', type: 'mono', align: 'right', required: true },
  { key: 'phase', label: 'Phase', width: '90px', type: 'chip' },
  { key: 'issues', label: 'Issues', width: '60px', type: 'issues' },
];

// Only non-autocomplete filters remain as FilterDef

function formatDate(iso: string | null | undefined): string {
  return formatDateOnly(iso, { month: 'short', day: 'numeric' }, '—');
}

export const LegacyClaimsTab: React.FC = () => {
  const { singularCap } = useSubjectLanguageVariants();
  const DEFAULT_COLUMNS = useMemo(() => getDefaultColumns(singularCap), [singularCap]);

  const navigate = useNavigate();
  const { clinicians, isLoading: cliniciansLoading } = useClinicians();
  const { data: payerStatsData, isLoading: payersLoading } = useBillingPayerOptionsQuery();
  const {
    activeSegment,
    setActiveSegment,
    filterValues,
    setFilterValue,
    page,
    setPage,
    perPage,
    setPerPage,
    dateRange,
    dateFrom,
    dateTo,
    onDateRangeChange,
    filtersOpen,
    toggleFilters,
  } = useTabFilters({ tabKey: 'claims', defaultSegment: 'all', defaultPerPage: 50 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showHistory, setShowHistory] = useState(false);

  const { allColumns, visibleKeys, visibleColumns, updatePreferences } =
    useColumnPreferences('claims', DEFAULT_COLUMNS);

  // Bulk action hooks. Batch submission is an async, polled job (the hook
  // resolves when the batch is QUEUED, not when claims are processed), so
  // this tab renders the same progress tray and terminal refetch as the
  // canonical ClaimsTab — the shared hook's model applies to both gate paths.
  const {
    activeBatchId,
    batchProgress,
    dismissBatch,
    isBatchSubmitting,
    isBatchTerminal,
    submitBatch,
  } = useBatchClaimSubmit();
  const { downloadBulkClaims, isDownloading: isBulkDownloading } = useBulkClaimX12Download();

  const queryParams = useMemo(
    () => ({
      phase: activeSegment !== 'all' ? activeSegment : undefined,
      patientId: filterValues.patientId || undefined,
      providerIds: filterValues.providerIds || undefined,
      payerUuid: filterValues.payerUuid || undefined,
      dateFrom,
      dateTo,
      sortBy: 'service_date_from' as const,
      sortOrder: 'desc' as const,
      page,
      perPage,
      includeSuperseded: showHistory || undefined,
    }),
    [activeSegment, filterValues, dateFrom, dateTo, page, perPage, showHistory],
  );

  const { data, isLoading, isFetching, refetch } = useClaimsV2Query(queryParams);

  /* This tab's list query (BillingV2Claims) is not covered by the
     BillingV2ClaimWork invalidation the hook fires at batch terminal state,
     so it re-reads explicitly — without this, submitted claims keep their
     pre-batch phase until the next natural refetch. */
  useEffect(() => {
    if (!isBatchTerminal) return;
    refetch();
  }, [isBatchTerminal, refetch]);

  const segments = useMemo(() => {
    return data?.segments ?? [];
  }, [data]);

  const rows = useMemo(() => {
    if (!data?.claims) return [];
    return data.claims.map((claim: ClaimV2Item) => ({
      id: claim.claim_id,
      claim_id: claim.claim_id,
      service_date: formatDate(claim.service_date),
      patient_name: claim.patient_name ?? '—',
      provider_name: claim.provider_name ?? '—',
      payer_name: claim.payer_name ?? '—',
      what_to_do: claim.what_to_do,
      what_to_do_severity: claim.what_to_do.severity,
      amount: formatCents(claim.total_charge_cents),
      phase: claim.phase,
      phase_label: claim.phase_label,
      issues: true,
      error_count: claim.error_count,
      warning_count: claim.warning_count,
      _superseded: Boolean(claim.superseded_by_claim_id),
      _claim_type: claim.claim_type ?? 'original',
      _parent_claim_id: claim.parent_claim_id ?? null,
      _raw: claim,
    }));
  }, [data]);

  // Derive selected claims for bulk actions
  const selectedClaims = useMemo(() => {
    return rows.filter(r => selectedIds.has(r.claim_id)).map(r => r._raw);
  }, [rows, selectedIds]);

  const readyClaimIds = useMemo(
    () => selectedClaims.filter(c => c.phase === 'ready' && (c.error_count ?? 0) === 0).map(c => c.claim_id),
    [selectedClaims],
  );

  const submittedClaimIds = useMemo(
    () => selectedClaims.filter(c => ['inFlight', 'paid', 'review'].includes(c.phase)).map(c => c.claim_id),
    [selectedClaims],
  );

  const bulkActions = useMemo((): SelectionAction[] => {
    const actions: SelectionAction[] = [];
    if (readyClaimIds.length > 0) {
      actions.push({
        label: `Submit ${readyClaimIds.length} Claim${readyClaimIds.length !== 1 ? 's' : ''}`,
        onClick: async () => {
          await submitBatch(readyClaimIds);
          setSelectedIds(new Set());
        },
        loading: isBatchSubmitting,
        /* One batch at a time — the tray below tracks the active one until
           it is dismissed, same as the canonical tab's selection gate. */
        disabled: Boolean(activeBatchId),
      });
    }
    if (submittedClaimIds.length > 0) {
      actions.push({
        label: `Download X12 (${submittedClaimIds.length})`,
        onClick: async () => {
          await downloadBulkClaims(submittedClaimIds);
        },
        variant: 'outlined',
        loading: isBulkDownloading,
      });
    }
    return actions;
  }, [activeBatchId, readyClaimIds, submittedClaimIds, submitBatch, downloadBulkClaims, isBatchSubmitting, isBulkDownloading]);

  const handleRowClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (row: Record<string, any>) => {
      const claimId = row.claim_id as string;
      navigate(`/jotbill/claim/${claimId}`);
    },
    [navigate],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCell = useCallback((column: ColumnDef, row: Record<string, any>) => {
    if (column.key === 'what_to_do') {
      const raw = row._raw as ClaimV2Item;
      if (!raw.era_triage?.needs_attention) return null;
      const state = getDispositionState(raw.attention_disposition);
      const indicator = state === 'active'
        ? <AttentionPulseDot triage={raw.era_triage} />
        : raw.attention_disposition
          ? <AttentionDispositionChip disposition={raw.attention_disposition} />
          : null;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          {indicator}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <CellContent column={column} value={row[column.key] as unknown} row={row} />
          </Box>
        </Box>
      );
    }

    if (column.type !== 'chip') return null;
    const claimType = row._claim_type as string | undefined;
    const isSuperseded = Boolean(row._superseded);
    if (!isSuperseded && claimType !== 'corrected' && claimType !== 'void') return null;

    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
        <CellContent column={column} value={row[column.key] as unknown} row={row} />
        {claimType === 'corrected' && (
          <Chip label="Correction" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 500, bgcolor: '#E0F2FE', color: '#0369A1', '& .MuiChip-label': { px: 0.5 } }} />
        )}
        {claimType === 'void' && (
          <Chip label="Void" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 500, bgcolor: '#FEE2E2', color: '#991B1B', '& .MuiChip-label': { px: 0.5 } }} />
        )}
        {isSuperseded && (
          <Chip label="Replaced" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 500, bgcolor: 'grey.100', color: 'text.disabled', '& .MuiChip-label': { px: 0.5 } }} />
        )}
      </Box>
    );
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rowSx = useCallback((row: Record<string, any>) => {
    if (row._superseded) return { opacity: 0.5 };
    const raw = row._raw as ClaimV2Item;
    const state = getDispositionState(raw.attention_disposition);
    if (state === 'dismissed' || state === 'snoozed') return { opacity: 0.65 };
    return {};
  }, []);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilterValue(key, value);
  }, [setFilterValue]);

  const handleSegmentChange = useCallback((key: string) => {
    setActiveSegment(key);
    setSelectedIds(new Set());
  }, [setActiveSegment]);

  const handlePatientSelect = useCallback((patient: Patient | undefined) => {
    setFilterValue('patientId', patient?.patient_id ?? '');
  }, [setFilterValue]);

  const handleProviderChange = useCallback((providerId: string) => {
    setFilterValue('providerIds', providerId);
  }, [setFilterValue]);

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rounded"  sx={{ height: 42, width: 400 }}/>
        <Skeleton variant="rounded"  sx={{ height: 400 }}/>
      </Box>
    );
  }

  if (activeSegment === 'review') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <SegmentControl segments={segments} active={activeSegment} onChange={handleSegmentChange} />
          <Tooltip title="Include previous versions of corrected or voided claims" arrow placement="top">
            <Typography
              variant="body2"
              onClick={() => setShowHistory(prev => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                color: showHistory ? 'primary.main' : 'text.disabled',
                cursor: 'pointer',
                userSelect: 'none',
                flexShrink: 0,
                fontWeight: showHistory ? 500 : 400,
                '&:hover': { color: 'text.secondary' },
              }}
            >
              <HistoryIcon sx={{ fontSize: 14 }} />
              Show history
            </Typography>
          </Tooltip>
        </Box>
        <ClaimReviewWorkspace />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SegmentControl segments={segments} active={activeSegment} onChange={handleSegmentChange} />
        <Tooltip title="Include previous versions of corrected or voided claims" arrow placement="top">
          <Typography
            variant="body2"
            onClick={() => setShowHistory(prev => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.75rem',
              color: showHistory ? 'primary.main' : 'text.disabled',
              cursor: 'pointer',
              userSelect: 'none',
              flexShrink: 0,
              fontWeight: showHistory ? 500 : 400,
              '&:hover': { color: 'text.secondary' },
            }}
          >
            <HistoryIcon sx={{ fontSize: 14 }} />
            Show history
          </Typography>
        </Tooltip>
      </Box>
      <TableToolbar
        summary={data?.summary ?? ''}
        allColumns={allColumns}
        visibleKeys={visibleKeys}
        onColumnsChange={updatePreferences}
        filtersOpen={filtersOpen}
        onToggleFilters={toggleFilters}
        paginationData={data}
        onPageChange={setPage}
        filterExtra={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Box sx={{ width: 200 }}>
              <PayerFilter
                payers={payerStatsData?.payers ?? []}
                isLoading={payersLoading}
                value={filterValues.payerUuid ?? ''}
                onChange={(uuid) => handleFilterChange('payerUuid', uuid)}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <PatientPicker
                onPatientSelect={handlePatientSelect}
                size="small"
                disableAddNew
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <Autocomplete<Clinician>
                size="small"
                options={clinicians}
                loading={cliniciansLoading}
                value={clinicians.find(c => c.userId === filterValues.providerIds) ?? null}
                onChange={(_e, val) => handleProviderChange(val?.userId ?? '')}
                getOptionLabel={opt => opt.name}
                isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
                renderInput={params => <TextField {...params} label="Provider" size="small" />}
              />
            </Box>
            <Box sx={{ width: 200 }}>
              <DateRangeSelector
                value={dateRange}
                onChange={onDateRangeChange}
                label="Date Range"
                size="small"
              />
            </Box>
          </Box>
        }
      />
      <DataTable
        columns={visibleColumns}
        rows={rows}
        onRowClick={handleRowClick}
        idKey="claim_id"
        isFetching={isFetching}
        renderRowEnd={row => <ClaimRowActions claim={row._raw as ClaimV2Item} />}
        renderCell={renderCell}
        rowSx={rowSx}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
      {data && (
        <Pagination
          data={data}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
        />
      )}
      {batchProgress ? (
        <ClaimBatchProgressTray
          progress={batchProgress}
          onDismiss={dismissBatch}
          onReviewClaim={claimId => navigate(`/jotbill/claim/${claimId}`)}
        />
      ) : (
        <SelectionToolbar
          count={selectedIds.size}
          actions={bulkActions}
          onClear={() => setSelectedIds(new Set())}
        />
      )}
    </Box>
  );
};

